import React, { useContext } from 'react';

import { useCustomDataColumnsQuery } from 'api/useCustomDataColumnsQuery';

/**
 * Context for managing custom person data keys in a React application.
 *
 * This context provides a shared state for storing and accessing custom data keys
 * associated with persons. The default value is an object containing an empty array
 * for the `customPersonDataKeys` property.
 *
 * @type {React.Context<{ customPersonDataKeys: Array<import('services/services').CustomDataColumn> | null }>}
 */
export const CustomPersonDataKeysContext = React.createContext({
    customPersonDataKeys: null
});

/**
 * CustomPersonDataKeysProvider is a React functional component that provides context for custom person data keys.
 * It fetches custom data columns using a query function and shares the retrieved data with its children components
 * through a context provider.
 *
 * @param {object} props - Props passed to the component.
 * @param {React.ReactNode} props.children - React components or elements that will have access to the provided context.
 * @returns {React.ReactNode} The context provider containing custom person data keys, wrapping the children components.
 */
export const CustomPersonDataKeysProvider = ({ children }) => {
    const { data: customDataKeys } = useCustomDataColumnsQuery({
        networkMode: 'offlineFirst',
        refetchOnWindowFocus: false,
        placeholderData: null
    });

    return (
        <CustomPersonDataKeysContext.Provider
            value={{
                customPersonDataKeys: customDataKeys
            }}
        >
            {children}
        </CustomPersonDataKeysContext.Provider>
    );
};

/**
 * A custom hook that retrieves an array of custom person data keys from the context.
 * If the context is not available or undefined, it returns an empty array.
 *
 * @returns {Array<import('services/services').CustomDataColumn> | null} An array of custom person data keys from the `CustomPersonDataKeysContext`.
 */
export const useCustomPersonDataKeys = () => {
    const context = useContext(CustomPersonDataKeysContext);

    if (!context) throw new Error('useCustomPersonDataKeys must be used within a CustomPersonDataKeysProvider');

    return context.customPersonDataKeys;
};

/**
 * A higher-order component (HOC) that injects custom person data keys into the wrapped component.
 *
 * This HOC retrieves custom person data keys using the `useCustomPersonDataKeys` hook
 * and provides them as a prop to the wrapped component under the name `customPersonDataKeys`.
 *
 * @param {React.ComponentType} Component - The component to be wrapped and enhanced with custom person data keys.
 * @returns {React.FunctionComponent} A new functional component that passes down
 * the `customPersonDataKeys` along with the existing props to the wrapped component.
 */
export const withCustomPersonDataKeys = Component => props => {
    const customPersonDataKeys = useCustomPersonDataKeys();

    return <Component {...props} customPersonDataKeys={customPersonDataKeys} />;
};
